// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React, { useState } from 'react';
import HeterosisSection from '@/components/HeterosisSection';

export function LxTView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="lxt-view">
      {data.interpretation && (
        <div className="bg-[#FDECEC] rounded-2xl p-5 border border-[#C0392B]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
        </div>
      )}

      {/* Chart - GCA & SCA heatmap */}
      {data.chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden">
          <div className="bg-[#C0392B]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#C0392B] uppercase tracking-wider">GCA Effects & SCA Heatmap</h4>
          </div>
          <img src={`data:image/png;base64,${data.chart}`} alt="LxT Chart" className="w-full" data-testid="lxt-chart" />
        </div>
      )}

      {/* ANOVA */}
      {data.anova_table && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">ANOVA Table</h4>
          <table className="w-full text-xs border-collapse" data-testid="lxt-anova">
            <thead><tr className="bg-[#C0392B] text-white">
              {['Source', 'DF', 'SS', 'MS', 'F', 'P', 'Sig'].map(h => (
                <th key={h} className="px-3 py-2 text-center font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {Object.entries(data.anova_table).map(([src, d], i) => (
                <tr key={src} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FDF5F5]'}>
                  <td className="px-3 py-2 font-medium text-left">{src}</td>
                  <td className="px-3 py-2 text-center font-mono">{d.DF ?? '-'}</td>
                  <td className="px-3 py-2 text-center font-mono">{typeof d.SS === 'number' ? d.SS.toFixed(4) : '-'}</td>
                  <td className="px-3 py-2 text-center font-mono">{typeof d.MS === 'number' ? d.MS.toFixed(4) : '-'}</td>
                  <td className="px-3 py-2 text-center font-mono">{typeof d.F === 'number' ? d.F.toFixed(4) : '-'}</td>
                  <td className="px-3 py-2 text-center font-mono">{typeof d.P === 'number' ? d.P.toFixed(6) : '-'}</td>
                  <td className="px-3 py-2 text-center">
                    {d.Sig && <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${d.Sig === '**' ? 'bg-emerald-500 text-white' : d.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{d.Sig}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* GCA Lines */}
        {data.gca_lines && (
          <div>
            <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">GCA Effects (Lines)</h4>
            <table className="w-full text-xs border-collapse" data-testid="lxt-gca-lines">
              <thead><tr className="bg-[#C0392B] text-white">
                <th className="px-3 py-2 text-left">Line</th><th className="px-3 py-2 text-center">GCA Effect</th><th className="px-3 py-2 text-center">Status</th>
              </tr></thead>
              <tbody>
                {Object.entries(data.gca_lines).map(([k, v], i) => (
                  <tr key={k} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FDF5F5]'}>
                    <td className="px-3 py-1.5 font-medium">{k}</td>
                    <td className="px-3 py-1.5 text-center font-mono">{v.toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-center">
                      <span className={`text-[10px] font-bold ${v > 0 ? 'text-green-600' : 'text-red-600'}`}>{v > 0 ? 'Good Combiner' : 'Poor Combiner'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* GCA Testers */}
        {data.gca_testers && (
          <div>
            <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">GCA Effects (Testers)</h4>
            <table className="w-full text-xs border-collapse" data-testid="lxt-gca-testers">
              <thead><tr className="bg-[#C0392B] text-white">
                <th className="px-3 py-2 text-left">Tester</th><th className="px-3 py-2 text-center">GCA Effect</th><th className="px-3 py-2 text-center">Status</th>
              </tr></thead>
              <tbody>
                {Object.entries(data.gca_testers).map(([k, v], i) => (
                  <tr key={k} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FDF5F5]'}>
                    <td className="px-3 py-1.5 font-medium">{k}</td>
                    <td className="px-3 py-1.5 text-center font-mono">{v.toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-center">
                      <span className={`text-[10px] font-bold ${v > 0 ? 'text-green-600' : 'text-red-600'}`}>{v > 0 ? 'Good Combiner' : 'Poor Combiner'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* SCA Effects */}
      {data.sca_effects && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">SCA Effects (Specific Crosses)</h4>
          <table className="w-full text-xs border-collapse" data-testid="lxt-sca">
            <thead><tr className="bg-[#C0392B] text-white">
              <th className="px-3 py-2 text-left">Cross</th><th className="px-3 py-2 text-center">SCA Effect</th><th className="px-3 py-2 text-center">Desirability</th>
            </tr></thead>
            <tbody>
              {Object.entries(data.sca_effects).sort((a, b) => b[1] - a[1]).map(([k, v], i) => (
                <tr key={k} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FDF5F5]'}>
                  <td className="px-3 py-1.5 font-medium">{k}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{v.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${v > 0.5 ? 'bg-green-100 text-green-700' : v < -0.5 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                      {v > 0.5 ? 'Desirable' : v < -0.5 ? 'Undesirable' : 'Neutral'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Variance Components */}
      {data.variance_components && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(data.variance_components).map(([k, v]) => (
            <div key={k} className="bg-[#C0392B]/5 rounded-xl px-4 py-3 text-center border border-[#C0392B]/15">
              <div className="text-lg font-bold text-[#C0392B]">{typeof v === 'number' ? v.toFixed(4) : v}</div>
              <div className="text-[10px] text-[#8A8580] uppercase">{k.replace(/_/g, ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {/* Genetic Conclusions */}
      {data.genetic_conclusions && (
        <div className="overflow-x-auto" data-testid="lxt-genetic-conclusions">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Genetic Conclusions</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#8E44AD] text-white">
              <th className="px-3 py-2 text-left">Parameter</th><th className="px-3 py-2 text-center">Value</th>
            </tr></thead>
            <tbody>
              {Object.entries(data.genetic_conclusions).map(([k, v], i) => (
                <tr key={k} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F0FF]'}>
                  <td className="px-3 py-1.5 font-medium">{k.replace(/_/g, ' ')}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{typeof v === 'number' ? v.toFixed(4) : String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Heterosis Analysis (MPH/BPH/SH) — appears when /api/analyze/line-x-tester
          attaches a `heterosis` field. Niranjan Phase A, Feb 2026. */}
      {data.heterosis && (
        <HeterosisSection
          heterosis={data.heterosis}
          designKey="lxt"
          analysisId={data.id || data._id}
          analysisType="line_x_tester"
          trait={data.trait_analyzed || data.trait}
        />
      )}
    </div>
  );
}

/* ============================================================
   DIALLEL (GRIFFING) VIEW — PHASE 3 COMPLETE RENDERING
============================================================ */


function DiallelFigure({ src, title, caption, testId }) {
  if (!src) return null;
  return (
    <div className="bg-white border border-[#E0EDE0] rounded-xl p-4 my-4" data-testid={testId}>
      <h5 className="text-sm font-bold text-[#1F4E2E] mb-2">{title}</h5>
      <img src={`data:image/png;base64,${src}`} alt={title} className="w-full max-w-2xl mx-auto" />
      {caption && <p className="text-xs italic text-[#6B8F71] mt-2 max-w-xl">{caption}</p>}
    </div>
  );
}


export function DiallelView({ data }) {
  const [verifyOpen, setVerifyOpen] = React.useState(false);
  if (!data) return null;

  const t = data.tables || {};
  const f = data.figures || {};
  const dv = data.data_validation || {};
  const vf = data.verification || {};

  const thCls = "px-3 py-2.5 text-center font-semibold text-[13px]";
  const tdCls = "px-3 py-2 text-center font-mono text-[13px]";
  const sectionHead = "text-base font-bold text-[#1F4E2E] border-b-2 border-[#2D8B57] pb-1.5 mb-4 mt-8";

  const pFmt = (v) => {
    if (v === null || v === undefined) return '—';
    return v < 0.001 ? '<0.001' : v.toFixed(4);
  };
  const nFmt = (v, d=4) => (typeof v === 'number' ? v.toFixed(d) : (v ?? '—'));

  return (
    <div className="space-y-4" data-testid="diallel-view">

      {/* ═══ SECTION 1: Data Validation Notices ═══ */}
      {dv.duplicate_columns_removed > 0 && (
        <div className="rounded-lg p-3 px-4 border" style={{ background: '#FFFDE7', borderColor: '#F9A825' }} data-testid="diallel-dup-notice">
          <span className="font-bold text-[13px]">&#9888; Duplicate columns detected and removed automatically.</span>
          <p className="text-[12px] mt-1 text-[#5D4037]">
            Removed: <strong>{(dv.dropped_column_names || []).join(', ')}</strong>.
            {' '}Analysis used <strong>{(dv.kept_columns || []).filter(c => !['Parent1','Parent2','Rep'].includes(c)).join(' and ')}</strong> only.
          </p>
        </div>
      )}
      {dv.missing_crosses > 0 && (
        <div className="rounded-lg p-3 px-4 border" style={{ background: '#FFEBEE', borderColor: '#EF5350' }} data-testid="diallel-missing-notice">
          <span className="font-bold text-[13px]">&#9888; Incomplete diallel data detected.</span>
          <p className="text-[12px] mt-1 text-[#B71C1C]">
            {dv.observed_crosses} of {dv.expected_crosses} required crosses are present.
            {' '}Missing: <strong>{(dv.missing_crosses_list || []).join(', ')}</strong>.
            {' '}Results may be statistically unreliable. Please add missing crosses and re-upload for valid analysis.
          </p>
        </div>
      )}

      {/* ═══ SECTION 2: Method & Summary Bar ═══ */}
      <div className="rounded-lg p-2.5 px-4 flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-[#1F4E2E]" style={{ background: '#F4FAF5' }} data-testid="diallel-summary-bar">
        <span><strong>Method:</strong> {data.method}</span>
        <span><strong>Parents:</strong> {dv.parents_detected} ({(data.parents || []).join(', ')})</span>
        <span><strong>Crosses:</strong> {dv.observed_crosses} of {dv.expected_crosses}</span>
        <span><strong>Trait:</strong> {data.trait_analyzed}</span>
        <span><strong>Replicates:</strong> {dv.replicates}</span>
      </div>

      {/* ═══ SECTION 3: Verification Panel (collapsed) ═══ */}
      <div className="border border-[#E0EDE0] rounded-lg overflow-hidden" data-testid="diallel-verification">
        <button onClick={() => setVerifyOpen(!verifyOpen)} className="w-full text-left px-4 py-2.5 bg-[#F7FBF7] hover:bg-[#EEF5EE] text-[13px] font-medium text-[#2D8B57] flex items-center gap-2 transition-colors">
          <span style={{ transform: verifyOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>&#9654;</span>
          View Calculation Verification
        </button>
        {verifyOpen && (
          <div className="px-4 py-3 text-[12px] font-mono text-[#333] space-y-1 bg-white border-t border-[#E0EDE0]">
            <div>GCA sum = {vf.gca_sum} &#10003;</div>
            <div>Degree of Dominance: {vf.degree_of_dominance_formula}</div>
            <div>Va formula: 4 x Vg (Griffing 1956, non-inbred parents F=0)</div>
            <div>Method: Griffing {vf.method_used}</div>
            <div>Crosses present: {vf.crosses_present}</div>
            <div className="text-[#2D8B57] font-bold">All checks passed &#10003;</div>
          </div>
        )}
      </div>

      {/* ═══ SECTION 4: TABLE 1 — ANOVA ═══ */}
      {t.anova && (
        <div>
          <h4 className={sectionHead} data-testid="diallel-t1-heading">Table 1. ANOVA Table</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-anova">
              <thead><tr className="bg-[#2D8B57] text-white">
                {['Source','df','SS','MS','F','p-value','Sig'].map(h => <th key={h} className={thCls}>{h}</th>)}
              </tr></thead>
              <tbody>
                {t.anova.map((row, i) => {
                  const bg = row.significant ? 'rgba(45,139,87,0.10)' : (i % 2 === 0 ? '#F7FBF7' : 'white');
                  return (
                    <tr key={row.Source} style={{ background: bg }}>
                      <td className="px-3 py-2 font-semibold text-left text-[13px]">{row.Source}</td>
                      <td className={tdCls}>{row.DF}</td>
                      <td className={tdCls}>{nFmt(row.SS)}</td>
                      <td className={tdCls}>{nFmt(row.MS)}</td>
                      <td className={tdCls}>{row.F !== null ? nFmt(row.F) : '—'}</td>
                      <td className={tdCls}>{row.p_value !== null ? pFmt(row.p_value) : '—'}</td>
                      <td className="px-3 py-2 text-center">
                        {row.Sig ? <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${row.Sig === '**' ? 'bg-emerald-600 text-white' : row.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{row.Sig}</span> : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Figure 6 — Proportional Pie (right after ANOVA) */}
          <DiallelFigure src={f.proportional_pie} title="Figure 6. Relative Contribution of GCA and SCA to Total Variation"
            caption="GCA contribution indicates additive gene action. SCA contribution indicates non-additive gene action. Larger SCA proportion recommends hybrid breeding."
            testId="diallel-fig6" />

          {/* SE & CD Table (after ANOVA) */}
          {t.se_cd && (
            <div className="mt-4">
              <h5 className="text-sm font-bold text-[#1F4E2E] mb-2">Standard Errors and Critical Differences</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px] border-collapse" data-testid="diallel-se-cd">
                  <thead><tr className="bg-[#2D8B57] text-white">
                    <th className={`${thCls} text-left`}>Parameter</th>
                    <th className={thCls}>GCA</th>
                    <th className={thCls}>SCA</th>
                  </tr></thead>
                  <tbody>
                    <tr className="bg-[#F7FBF7]">
                      <td className="px-3 py-2 font-medium text-left text-[13px]">SE of effects</td>
                      <td className={tdCls}>{nFmt(t.se_cd.SE_gi)}</td>
                      <td className={tdCls}>{nFmt(t.se_cd.SE_sij)}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-3 py-2 font-medium text-left text-[13px]">SE of difference between 2 effects</td>
                      <td className={tdCls}>{nFmt(t.se_cd.SE_diff_GCA)}</td>
                      <td className={tdCls}>{nFmt(t.se_cd.SE_diff_SCA)}</td>
                    </tr>
                    <tr className="bg-[#F7FBF7]">
                      <td className="px-3 py-2 font-medium text-left text-[13px]">CD at 5% (t={nFmt(t.se_cd.t_value_5pct)})</td>
                      <td className={`${tdCls} font-bold`}>{nFmt(t.se_cd.CD_GCA_5pct)}</td>
                      <td className={`${tdCls} font-bold`}>{nFmt(t.se_cd.CD_SCA_5pct)}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-3 py-2 font-medium text-left text-[13px]">CD at 1% (t={nFmt(t.se_cd.t_value_1pct)})</td>
                      <td className={`${tdCls} font-bold`}>{nFmt(t.se_cd.CD_GCA_1pct)}</td>
                      <td className={`${tdCls} font-bold`}>{nFmt(t.se_cd.CD_SCA_1pct)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] italic text-[#6B8F71] mt-1">df(error) = {t.se_cd.df_error}</p>
            </div>
          )}
        </div>
      )}

      {/* ═══ SECTION 5: TABLE 2 — Cross Means Matrix ═══ */}
      {t.cross_means && (
        <div>
          <h4 className={sectionHead} data-testid="diallel-t2-heading">Table 2. Diallel Cross Means</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-cross-means">
              <thead><tr className="bg-[#2D8B57] text-white">
                <th className={thCls}></th>
                {(t.cross_means.parents || []).map(p => <th key={p} className={thCls}>{p}</th>)}
              </tr></thead>
              <tbody>
                {(t.cross_means.matrix || []).map((row, ri) => (
                  <tr key={row.Parent} className={ri % 2 === 0 ? 'bg-[#F7FBF7]' : 'bg-white'}>
                    <td className="px-3 py-2 font-semibold text-left bg-[#2D8B57] text-white text-[13px]">{row.Parent}</td>
                    {(t.cross_means.parents || []).map((col, ci) => {
                      const v = row[col];
                      const isSelf = ri === ci;
                      const isBest = typeof v === 'number' && `${row.Parent} x ${col}` === t.cross_means.best_cross;
                      const isWorst = typeof v === 'number' && `${row.Parent} x ${col}` === t.cross_means.worst_cross;
                      let cellBg = isSelf ? '#F0F0F0' : 'transparent';
                      if (isBest) cellBg = 'rgba(45,139,87,0.18)';
                      if (isWorst) cellBg = 'rgba(198,40,40,0.12)';
                      return (
                        <td key={col} className={`${tdCls} ${isBest ? 'font-bold' : ''}`} style={{ background: cellBg }}>
                          {typeof v === 'number' ? v.toFixed(2) : (v || '—')}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[13px] font-bold text-[#1F4E2E] mt-2">Grand Mean: {nFmt(t.cross_means.grand_mean, 4)}</p>
          {/* Figure 8 — Cross Performance */}
          <DiallelFigure src={f.cross_performance} title="Figure 8. Cross Mean Performance"
            caption="Parent self-crosses shown in lighter bars. F1 crosses in solid bars. Grand mean reference line shown."
            testId="diallel-fig8" />
        </div>
      )}

      {/* ═══ SECTION 6: TABLE 3 — GCA Effects ═══ */}
      {t.gca_effects && (
        <div>
          <h4 className={sectionHead} data-testid="diallel-t3-heading">Table 3. GCA Effects</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-gca">
              <thead><tr className="bg-[#2D8B57] text-white">
                {['Rank','Parent','GCA Effect','SE','t-statistic','p-value','Classification'].map(h => <th key={h} className={thCls}>{h}</th>)}
              </tr></thead>
              <tbody>
                {t.gca_effects.map((row) => {
                  const bg = row.positive ? 'rgba(45,139,87,0.08)' : 'rgba(198,40,40,0.06)';
                  return (
                    <tr key={row.Parent} style={{ background: bg }}>
                      <td className={tdCls}>{row.Rank}</td>
                      <td className="px-3 py-2 font-semibold text-left text-[13px]">{row.Parent}</td>
                      <td className={tdCls}>{nFmt(row.GCA_effect)}</td>
                      <td className={tdCls}>{nFmt(row.SE)}</td>
                      <td className={tdCls}>{nFmt(row.t_statistic)}</td>
                      <td className={tdCls}>{pFmt(row.p_value)}</td>
                      <td className="px-3 py-2 text-center text-[12px]">{row.Classification}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] italic text-[#2D8B57] mt-2" data-testid="diallel-gca-sum">
            Sum of GCA effects = {t.gca_effects.reduce((s, r) => s + r.GCA_effect, 0).toFixed(4)} &#10003;
          </p>
          {/* Figure 1 — GCA Bar */}
          <DiallelFigure src={f.gca_bar} title="Figure 1. General Combining Ability Effects"
            caption="Green bars indicate good combiners (positive GCA). Red bars indicate poor combiners (negative GCA). Error bars represent standard error. Parents sorted highest to lowest GCA."
            testId="diallel-fig1" />
          {/* Figure 4 — Per-se vs GCA Scatter */}
          <DiallelFigure src={f.perse_scatter} title="Figure 4. Per-se Performance vs GCA — Parent Classification"
            caption="Top-right quadrant (high mean, high GCA) indicates ideal parents for both direct selection and hybridization."
            testId="diallel-fig4" />
        </div>
      )}

      {/* ═══ SECTION 7: TABLE 4 — SCA Effects ═══ */}
      {t.sca_effects && (
        <div>
          <h4 className={sectionHead} data-testid="diallel-t4-heading">Table 4. SCA Effects</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-sca">
              <thead><tr className="bg-[#2D8B57] text-white">
                {['Cross','SCA Effect','SE','t-statistic','p-value','Significance'].map(h => <th key={h} className={thCls}>{h}</th>)}
              </tr></thead>
              <tbody>
                {t.sca_effects.map((row, i) => {
                  const isBest = i === 0;
                  const bg = isBest ? 'rgba(45,139,87,0.12)' : (i % 2 === 0 ? '#F7FBF7' : 'white');
                  return (
                    <tr key={row.Cross} style={{ background: bg }}>
                      <td className={`px-3 py-2 font-semibold text-left text-[13px] ${isBest ? 'text-[#2D8B57]' : ''}`}>{row.Cross}</td>
                      <td className={tdCls}>{nFmt(row.SCA_effect)}</td>
                      <td className={tdCls}>{nFmt(row.SE)}</td>
                      <td className={tdCls}>{nFmt(row.t_statistic)}</td>
                      <td className={tdCls}>{pFmt(row.p_value)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${row.Significance === '**' ? 'bg-emerald-600 text-white' : row.Significance === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{row.Significance}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Figure 2 — SCA Heatmap */}
          <DiallelFigure src={f.sca_heatmap} title="Figure 2. SCA Effects Heatmap"
            caption="Warm colors indicate high positive SCA (favorable cross combinations). Cool colors indicate negative SCA. Best cross highlighted with bold border."
            testId="diallel-fig2" />
          {/* Figure 5 — SCA Bar */}
          <DiallelFigure src={f.sca_bar} title="Figure 5. SCA Effects — All Crosses"
            caption="Positive SCA (green) indicates crosses performing better than expected from GCA alone. Sorted highest to lowest."
            testId="diallel-fig5" />
        </div>
      )}

      {/* ═══ SECTION 8: TABLE 5 — Variance Components ═══ */}
      {t.variance_components && (
        <div>
          <h4 className={sectionHead} data-testid="diallel-t5-heading">Table 5. Variance Components</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-variance">
              <thead><tr className="bg-[#2D8B57] text-white">
                <th className={`${thCls} text-left`}>Parameter</th><th className={thCls}>Value</th>
              </tr></thead>
              <tbody>
                {[
                  ['GCA Variance (Vg)', t.variance_components.GCA_variance_Vg],
                  ['SCA Variance (Vs)', t.variance_components.SCA_variance_Vs],
                  ['Error Variance (Ve)', t.variance_components.Error_variance_Ve],
                  ['GCA/SCA Ratio', t.variance_components.GCA_SCA_ratio],
                  ['Additive Variance (Va = 4 x Vg)', t.variance_components.Additive_variance_Va],
                  ['Dominance Variance (Vd = Vs)', t.variance_components.Dominance_variance_Vd],
                  ['Degree of Dominance = sqrt(Vd/Va)', `${nFmt(t.variance_components.Degree_of_dominance)} — ${t.variance_components.Dominance_classification}`],
                ].map(([label, val], i) => {
                  const isDod = label.includes('Degree');
                  return (
                    <tr key={label} style={{ background: isDod ? 'rgba(45,139,87,0.10)' : (i % 2 === 0 ? '#F7FBF7' : 'white') }}>
                      <td className="px-3 py-2 font-medium text-left text-[13px]">{label}</td>
                      <td className={`${tdCls} ${isDod ? 'font-bold text-[#1F4E2E]' : ''}`}>{typeof val === 'number' ? nFmt(val, 6) : val}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Figure 3 — Hayman Vr-Wr Graph */}
          {f.vr_wr_graph ? (
            <DiallelFigure src={f.vr_wr_graph} title="Figure 3. Hayman Vr-Wr Graph (1954) — Gene Action Analysis"
              caption="Each point represents one parent array. Parents near the origin carry predominantly dominant alleles. Parents far from origin carry predominantly recessive alleles. The regression slope indicates the type of gene action present."
              testId="diallel-fig3" />
          ) : f.vr_wr_note ? (
            <p className="text-xs italic text-[#6B8F71] mt-3 bg-[#F7FBF7] p-3 rounded-lg">{f.vr_wr_note}</p>
          ) : null}
        </div>
      )}

      {/* ═══ SECTION 9: TABLE 6 — Genetic Parameters ═══ */}
      {t.genetic_parameters && (
        <div>
          <h4 className={sectionHead} data-testid="diallel-t6-heading">Table 6. Genetic Parameters</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-genetic-params">
              <thead><tr className="bg-[#2D8B57] text-white">
                <th className={`${thCls} text-left`}>Parameter</th><th className={thCls}>Value</th>
              </tr></thead>
              <tbody>
                {[
                  ['h\u00B2 Narrow Sense', `${nFmt(t.genetic_parameters.h2_narrow_sense_pct, 2)}%`, false, true],
                  ['h\u00B2 Broad Sense', `${nFmt(t.genetic_parameters.h2_broad_sense_pct, 2)}%`, false, false],
                  ['GCA Contribution', `${nFmt(t.genetic_parameters.GCA_contribution_pct, 2)}%`, false, false],
                  ['SCA Contribution', `${nFmt(t.genetic_parameters.SCA_contribution_pct, 2)}%`, false, false],
                  ['Gene Action', t.genetic_parameters.Gene_action, true, false],
                  ['Breeding Strategy', t.genetic_parameters.Breeding_strategy, false, false],
                ].map(([label, val, isGene, isH2], i) => {
                  let bg = i % 2 === 0 ? '#F7FBF7' : 'white';
                  if (isGene) bg = t.genetic_parameters.Gene_action?.includes('Additive') ? 'rgba(45,139,87,0.10)' : 'rgba(230,81,0,0.08)';
                  if (isH2) bg = 'rgba(45,139,87,0.06)';
                  return (
                    <tr key={label} style={{ background: bg }}>
                      <td className="px-3 py-2 font-medium text-left text-[13px]">{label}</td>
                      <td className={`${tdCls} ${label === 'Breeding Strategy' ? 'font-bold' : ''}`}>{val}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ SECTION 10: TABLE 7 — Best Combinations ═══ */}
      {t.best_combinations && t.best_combinations.length > 0 && (
        <div>
          <h4 className={sectionHead} data-testid="diallel-t7-heading">Table 7. Best Cross Combinations</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-best-combos">
              <thead><tr className="bg-[#2D8B57] text-white">
                {['Rank','Cross','Mean','SCA','GCA P1','GCA P2','Recommendation'].map(h => <th key={h} className={thCls}>{h}</th>)}
              </tr></thead>
              <tbody>
                {t.best_combinations.map((row, i) => {
                  const isDoubly = row.Recommendation?.includes('Doubly');
                  const isCaution = row.Recommendation?.includes('Caution');
                  const isNegSca = row.Recommendation?.includes('negative SCA');
                  let bg = i % 2 === 0 ? '#F7FBF7' : 'white';
                  if (isDoubly) bg = 'rgba(45,139,87,0.12)';
                  if (isCaution) bg = 'rgba(255,152,0,0.08)';
                  if (isNegSca) bg = 'rgba(255,235,59,0.15)';
                  return (
                    <tr key={row.Cross} style={{ background: bg }}>
                      <td className={tdCls}>{row.Rank}</td>
                      <td className="px-3 py-2 font-semibold text-left text-[13px]">
                        {isDoubly && <span className="mr-1">&#9733;</span>}{row.Cross}
                      </td>
                      <td className={tdCls}>{row.Cross_mean !== null ? nFmt(row.Cross_mean, 2) : 'NA'}</td>
                      <td className={tdCls}>{nFmt(row.SCA)}</td>
                      <td className={tdCls}>{nFmt(row.GCA_Parent1)}</td>
                      <td className={tdCls}>{nFmt(row.GCA_Parent2)}</td>
                      <td className="px-3 py-2 text-left text-[11px] max-w-xs leading-snug">
                        {isCaution && <span className="mr-1">&#9888;</span>}
                        {row.Recommendation}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ SECTION 11: TABLE 8 — Parent Ranking ═══ */}
      {t.parent_ranking && (
        <div>
          <h4 className={sectionHead} data-testid="diallel-t8-heading">Table 8. Parent Ranking</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-parent-ranking">
              <thead><tr className="bg-[#2D8B57] text-white">
                {['Rank','Parent','Mean','GCA','Classification'].map(h => <th key={h} className={thCls}>{h}</th>)}
              </tr></thead>
              <tbody>
                {t.parent_ranking.map((row) => {
                  const isBest = row.Classification === 'Best parent';
                  const isAvoid = row.Classification === 'Avoid';
                  let bg = 'white';
                  if (isBest) bg = 'rgba(45,139,87,0.10)';
                  if (isAvoid) bg = 'rgba(198,40,40,0.08)';
                  return (
                    <tr key={row.Parent} style={{ background: bg }}>
                      <td className={tdCls}>{row.Rank}</td>
                      <td className="px-3 py-2 font-semibold text-left text-[13px]">{row.Parent}</td>
                      <td className={tdCls}>{nFmt(row.Mean, 2)}</td>
                      <td className={tdCls}>{nFmt(row.GCA)}</td>
                      <td className={`px-3 py-2 text-center text-[12px] font-bold ${isBest ? 'text-[#2D8B57]' : isAvoid ? 'text-[#C62828]' : 'text-[#E65100]'}`}>
                        {row.Classification}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ SECTION 12: Interpretation Summary ═══ */}
      {data.interpretation && (
        <div className="mt-8">
          <h4 className={sectionHead} data-testid="diallel-interp-heading">Interpretation Summary</h4>
          <div className="bg-[#F4FAF5] rounded-xl p-5 border border-[#D0E8D0]">
            <p className="text-[13px] text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
          </div>
        </div>
      )}

      {/* ═══ Reciprocal Effects (Methods I & III) — kept for backward compat ═══ */}
      {data.reciprocal_effects && Object.keys(data.reciprocal_effects).length > 0 && (
        <div className="mt-6">
          <h4 className={sectionHead}>Reciprocal Effects</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="diallel-reciprocal">
              <thead><tr className="bg-[#2D8B57] text-white">
                <th className={`${thCls} text-left`}>Cross</th><th className={thCls}>Reciprocal Effect</th><th className={thCls}>Direction</th>
              </tr></thead>
              <tbody>
                {Object.entries(data.reciprocal_effects).map(([cross, v], i) => (
                  <tr key={cross} className={i % 2 === 0 ? 'bg-[#F7FBF7]' : 'bg-white'}>
                    <td className="px-3 py-2 font-medium text-left">{cross}</td>
                    <td className={tdCls}>{nFmt(v)}</td>
                    <td className="px-3 py-2 text-center text-[11px] font-bold" style={{ color: v > 0 ? '#1565C0' : '#C62828' }}>{v > 0 ? 'Female > Male' : 'Male > Female'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ MULTI-TRAIT RESULTS ═══ */}
      {data.multi_trait_results && Object.keys(data.multi_trait_results).length > 0 && (
        <div className="mt-8">
          <h4 className={sectionHead} data-testid="diallel-multi-trait-heading">Additional Trait Analyses</h4>
          <p className="text-[12px] text-[#6B8F71] mb-4">
            Traits analyzed: {(data.all_traits_analyzed || []).join(', ')}
          </p>
          {Object.entries(data.multi_trait_results).map(([traitName, traitData]) => {
            if (!traitData || traitData.error) return (
              <div key={traitName} className="bg-[#FFEBEE] rounded-lg p-3 mb-3 text-[13px]">
                <strong>{traitName}:</strong> {traitData?.error || 'Analysis failed'}
              </div>
            );
            const tt = traitData.tables || {};
            return (
              <div key={traitName} className="mb-8 border border-[#E0EDE0] rounded-xl p-4" data-testid={`diallel-trait-${traitName}`}>
                <h5 className="text-[15px] font-bold text-[#1F4E2E] mb-3 pb-2 border-b border-[#2D8B57]">{traitName}</h5>
                {/* GCA Effects */}
                {tt.gca_effects && (
                  <div className="mb-4">
                    <h6 className="text-[13px] font-semibold text-[#1F4E2E] mb-2">GCA Effects — {traitName}</h6>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[13px] border-collapse">
                        <thead><tr className="bg-[#2D8B57] text-white">
                          {['Rank','Parent','GCA Effect','SE','t-stat','p-value','Class'].map(h => <th key={h} className={thCls}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {tt.gca_effects.map((row) => (
                            <tr key={row.Parent} style={{ background: row.positive ? 'rgba(45,139,87,0.08)' : 'rgba(198,40,40,0.06)' }}>
                              <td className={tdCls}>{row.Rank}</td>
                              <td className="px-3 py-2 font-semibold text-left text-[13px]">{row.Parent}</td>
                              <td className={tdCls}>{nFmt(row.GCA_effect)}</td>
                              <td className={tdCls}>{nFmt(row.SE)}</td>
                              <td className={tdCls}>{nFmt(row.t_statistic)}</td>
                              <td className={tdCls}>{pFmt(row.p_value)}</td>
                              <td className="px-3 py-2 text-center text-[12px]">{row.Classification}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[12px] italic text-[#2D8B57] mt-1">
                      Sum of GCA = {tt.gca_effects.reduce((s, r) => s + r.GCA_effect, 0).toFixed(4)} &#10003;
                    </p>
                  </div>
                )}
                {/* SCA Effects */}
                {tt.sca_effects && (
                  <div className="mb-4">
                    <h6 className="text-[13px] font-semibold text-[#1F4E2E] mb-2">SCA Effects — {traitName}</h6>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[13px] border-collapse">
                        <thead><tr className="bg-[#2D8B57] text-white">
                          {['Cross','SCA Effect','SE','t-stat','p-value','Sig'].map(h => <th key={h} className={thCls}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {tt.sca_effects.map((row, i) => (
                            <tr key={row.Cross} className={i % 2 === 0 ? 'bg-[#F7FBF7]' : 'bg-white'}>
                              <td className="px-3 py-2 font-semibold text-left text-[13px]">{row.Cross}</td>
                              <td className={tdCls}>{nFmt(row.SCA_effect)}</td>
                              <td className={tdCls}>{nFmt(row.SE)}</td>
                              <td className={tdCls}>{nFmt(row.t_statistic)}</td>
                              <td className={tdCls}>{pFmt(row.p_value)}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${row.Significance === '**' ? 'bg-emerald-600 text-white' : row.Significance === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{row.Significance}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {/* SE & CD for this trait */}
                {tt.se_cd && (
                  <div className="mb-4">
                    <h6 className="text-[13px] font-semibold text-[#1F4E2E] mb-2">SE & CD — {traitName}</h6>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[13px] border-collapse">
                        <thead><tr className="bg-[#2D8B57] text-white">
                          <th className={`${thCls} text-left`}>Parameter</th><th className={thCls}>GCA</th><th className={thCls}>SCA</th>
                        </tr></thead>
                        <tbody>
                          <tr className="bg-[#F7FBF7]"><td className="px-3 py-2 text-left text-[13px]">SE of effects</td><td className={tdCls}>{nFmt(tt.se_cd.SE_gi)}</td><td className={tdCls}>{nFmt(tt.se_cd.SE_sij)}</td></tr>
                          <tr className="bg-white"><td className="px-3 py-2 text-left text-[13px]">SE of difference</td><td className={tdCls}>{nFmt(tt.se_cd.SE_diff_GCA)}</td><td className={tdCls}>{nFmt(tt.se_cd.SE_diff_SCA)}</td></tr>
                          <tr className="bg-[#F7FBF7]"><td className="px-3 py-2 text-left text-[13px]">CD at 5%</td><td className={`${tdCls} font-bold`}>{nFmt(tt.se_cd.CD_GCA_5pct)}</td><td className={`${tdCls} font-bold`}>{nFmt(tt.se_cd.CD_SCA_5pct)}</td></tr>
                          <tr className="bg-white"><td className="px-3 py-2 text-left text-[13px]">CD at 1%</td><td className={`${tdCls} font-bold`}>{nFmt(tt.se_cd.CD_GCA_1pct)}</td><td className={`${tdCls} font-bold`}>{nFmt(tt.se_cd.CD_SCA_1pct)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {/* Variance Components summary */}
                {tt.variance_components && (
                  <p className="text-[12px] text-[#555] mt-1">
                    Va = {nFmt(tt.variance_components.Additive_variance_Va, 4)}, Vd = {nFmt(tt.variance_components.Dominance_variance_Vd, 4)}, DoD = {nFmt(tt.variance_components.Degree_of_dominance)} ({tt.variance_components.Dominance_classification})
                  </p>
                )}
                {/* Interpretation */}
                {traitData.interpretation && (
                  <div className="bg-[#F4FAF5] rounded-lg p-3 mt-3 border border-[#D0E8D0]">
                    <p className="text-[12px] text-[#2C2A28] whitespace-pre-line">{traitData.interpretation}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Heterosis Analysis (MPH/BPH/SH) for the primary trait — appears
          when /api/analyze/diallel(-method-1|-method-3) attaches a
          `heterosis` field. Niranjan Phase A, Feb 2026. */}
      {data.heterosis && (
        <HeterosisSection
          heterosis={data.heterosis}
          designKey="diallel"
          analysisId={data.id || data._id}
          analysisType="diallel_griffing"
          trait={data.trait_analyzed || data.trait}
        />
      )}
    </div>
  );
}

/* ============================================================
   MET META-ANALYSIS VIEW
============================================================ */
